// When symptoms show up, as a number the site can actually compute with.
//
// src/lib/diseases.ts already carries a sourced `onsetWindow` sentence for all eleven
// conditions ("3 to 30 days after the tick bite"). Every one of them was display-only,
// so the site held the answer to "when am I clear?" and could not give it.
//
// The windows below are authored literals, not runtime parses of that prose. Parsing
// sentences is how false precision gets invented; a literal can be read against the
// sentence beside it, and onset.test.ts does exactly that against diseases.ts on every
// run, so the two cannot drift.
//
// This table is deliberately standalone rather than a field on Disease: the bite flow
// needs the numbers on a phone that may be offline, and importing diseases.ts would
// pull ~950 lines of prose into that page for eleven pairs of integers.

export interface OnsetWindow {
  /** Earliest published day, counted from the bite. Omitted where no source states one. */
  minDays?: number;
  /** Latest published day. Omitted where the sources decline to give one. */
  maxDays?: number;
  /**
   * True when published cases are known to fall outside the window above, so passing
   * the last day is not an all-clear. Never let the UI imply "you're safe now".
   */
  openEnded?: boolean;
}

export interface OnsetEntry {
  /** Joins src/lib/diseases.ts by slug. */
  slug: string;
  /**
   * A short label for a cramped timeline row. May be shorter than the disease's
   * canonical name in diseases.ts ("STARI" for "Southern Tick-Associated Rash
   * Illness (STARI)"), but onset.test.ts requires it to still be part of that name.
   */
  shortName: string;
  /** Absent when no source publishes a bite-to-symptom window. See `note`. */
  window?: OnsetWindow;
  /** Why there is no window, or what the window does not cover. Displayed as-is. */
  note?: string;
  /** The short version of what to look for. Full lists live on the disease page. */
  watchFor: string;
}

export const ONSET: readonly OnsetEntry[] = [
  {
    slug: "ehrlichiosis",
    shortName: "Ehrlichiosis",
    window: { minDays: 5, maxDays: 14 },
    watchFor: "Fever, severe headache, body aches, fatigue",
  },
  {
    slug: "stari",
    shortName: "STARI",
    window: { maxDays: 14 },
    note: "The CDC gives about 7 days as typical; reported timing runs from a few days to roughly two weeks, with no firm earliest day.",
    watchFor: "An expanding circular rash around the bite",
  },
  {
    slug: "rocky-mountain-spotted-fever",
    shortName: "Rocky Mountain spotted fever",
    window: { minDays: 3, maxDays: 12 },
    watchFor: "Fever, severe headache, rash (do not wait for the rash)",
  },
  {
    slug: "tularemia",
    shortName: "Tularemia",
    window: { minDays: 1, maxDays: 21 },
    note: "Onset is usually 3–5 days; 1–21 days is the full reported range.",
    watchFor: "Sudden fever, chills, a skin ulcer at the bite, swollen glands",
  },
  {
    slug: "lyme-disease",
    shortName: "Lyme disease",
    window: { minDays: 3, maxDays: 30 },
    watchFor: "An expanding rash, fever, fatigue, joint aches",
  },
  {
    slug: "anaplasmosis",
    shortName: "Anaplasmosis",
    window: { minDays: 5, maxDays: 14 },
    watchFor: "Fever, chills, severe headache, muscle aches",
  },
  {
    slug: "babesiosis",
    shortName: "Babesiosis",
    window: { minDays: 7, maxDays: 28, openEnded: true },
    note: "Symptoms can first appear or come back months later, especially with a weakened immune system.",
    watchFor: "Fever, chills, sweats, fatigue, dark urine",
  },
  {
    slug: "powassan-virus",
    shortName: "Powassan virus",
    window: { minDays: 7, maxDays: 30 },
    watchFor: "Fever, headache, vomiting, weakness, confusion",
  },
  {
    slug: "heartland-virus",
    shortName: "Heartland virus",
    note: "The CDC describes a few days to several weeks and has not published a precise incubation period.",
    watchFor: "Fever, fatigue, low appetite, headache, muscle aches",
  },
  {
    slug: "bourbon-virus",
    shortName: "Bourbon virus",
    note: "Not firmly established by the CDC. Reported cases began within days to a couple of weeks.",
    watchFor: "Fever, tiredness, rash, headache, body aches",
  },
  {
    slug: "alpha-gal-syndrome",
    shortName: "Alpha-gal syndrome",
    note: "AGS runs on a different clock. Reactions come 2–6 hours after eating red meat or dairy, not on a fixed number of days after the bite. Sensitivity can develop well after a bite heals. There is no window to count down here.",
    watchFor: "Hives, stomach upset or swelling hours after red meat or dairy",
  },
];

const BY_SLUG = new Map(ONSET.map((e) => [e.slug, e]));

export function getOnset(slug: string): OnsetEntry | undefined {
  return BY_SLUG.get(slug);
}

export interface WatchItem extends OnsetEntry {
  /** Day the published window opens, counted from the bite. */
  opensOnDay?: number;
  /** Day it closes. */
  closesOnDay?: number;
  /** True while today falls inside the window. */
  active: boolean;
  /** True once the window has closed AND the sources treat it as closed. */
  passed: boolean;
}

const DAY_MS = 86_400_000;

/** Whole days between two dates, floor'd. Local midnight to local midnight. */
export function daysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.floor((b - a) / DAY_MS);
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Build the watch list for one bite.
 *
 * `slugs` restricts the list to the conditions a given tick actually transmits — the
 * whole point of asking which tick it was. Pass undefined to show everything.
 *
 * Nothing here diagnoses. It restates published windows against a date the user typed,
 * and a closed window is only ever reported as closed when the source supports it.
 */
export function buildWatchList(
  bitten: Date,
  today: Date,
  slugs?: readonly string[],
): { items: WatchItem[]; dayNumber: number } {
  const dayNumber = daysBetween(bitten, today);
  const allow = slugs ? new Set(slugs) : undefined;

  const items = ONSET.filter((e) => !allow || allow.has(e.slug)).map((e): WatchItem => {
    const opensOnDay = e.window?.minDays;
    const closesOnDay = e.window?.maxDays;
    const started = opensOnDay === undefined || dayNumber >= opensOnDay;
    const ended = closesOnDay !== undefined && dayNumber > closesOnDay;
    return {
      ...e,
      opensOnDay,
      closesOnDay,
      active: started && !ended,
      passed: ended && !e.window?.openEnded,
    };
  });

  items.sort((a, b) => {
    const rank = (i: WatchItem) => (i.passed ? 2 : i.active ? 0 : 1);
    return (
      rank(a) - rank(b) ||
      (a.closesOnDay ?? Number.MAX_SAFE_INTEGER) - (b.closesOnDay ?? Number.MAX_SAFE_INTEGER) ||
      a.shortName.localeCompare(b.shortName)
    );
  });

  return { items, dayNumber };
}

/** The last day any published window stays open, for "watch until" copy. */
export function longestWindowDays(slugs?: readonly string[]): number | undefined {
  const allow = slugs ? new Set(slugs) : undefined;
  const days = ONSET.filter((e) => !allow || allow.has(e.slug))
    .map((e) => e.window?.maxDays)
    .filter((d): d is number => d !== undefined);
  return days.length ? Math.max(...days) : undefined;
}
